import { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { qrMatrix } from "@equipped/core/qr/qrMatrix";
import { qrMatrixToSvg } from "@equipped/core/qr/qrSvg";
import { MainCard } from "../components/MainCard";
import { downloadQrPng, downloadQrSvg } from "../lib/qrDownload";

const QR_RENDER_OPTS = { moduleSize: 8, quietZone: 4 } as const;

/**
 * QR code generator card (docs/356-ADMIN-UTILITIES-PAGE.md v0's QR slice — the "utilities"
 * page). Paste a URL → render a QR live in the browser → **Copy** the URL / **Download**
 * PNG or SVG. Reuses the existing pure `@equipped/core/qr/qrMatrix` (docs/173, the SAME
 * encoder the consumer app's own catalog-QR share sheet uses) — no new QR dependency, per
 * the repo's no-new-dep norm (docs/041/074/173). The matrix→SVG-string math
 * (`qrMatrixToSvg`) is also pure/core (unit-tested); only the download-to-file plumbing
 * (`../lib/qrDownload.ts`, `<canvas>`/`Blob`) is browser-only admin-app code.
 *
 * **No tracked-short-link, no usage counter, no new Worker route.** docs/356 §3.2's
 * "generate a `GET /r/{slug}` redirect + a `short_links` D1 hit-counter" is a LATER slice —
 * this card encodes whatever URL is typed in directly, with no server round-trip at all.
 *
 * Gated by docs/358's `feature: "utilities"` (see `../layout/navItems.ts`) — owner-on by
 * default (`ownerDefault: true` in `packages/core/admin/adminFeatures.ts`), off for a
 * typical provider deployment (`providerDefault: false`) unless explicitly enabled via that
 * deployment's `AdminFeatureConfig`.
 */
export function UtilitiesPage() {
  const [url, setUrl] = useState("");
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const trimmed = url.trim();
  const matrix = useMemo(() => (trimmed ? qrMatrix(trimmed) : null), [trimmed]);
  const svgMarkup = useMemo(() => (matrix ? qrMatrixToSvg(matrix, QR_RENDER_OPTS) : null), [matrix]);

  const handleCopy = async () => {
    if (!trimmed) return;
    try {
      await navigator.clipboard.writeText(trimmed);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
    setTimeout(() => setCopyState("idle"), 2000);
  };

  const handleDownloadSvg = () => {
    if (!svgMarkup) return;
    setDownloadError(null);
    downloadQrSvg(svgMarkup, "qr-code.svg");
  };

  const handleDownloadPng = () => {
    if (!matrix) return;
    setDownloadError(null);
    downloadQrPng(matrix, "qr-code.png", QR_RENDER_OPTS).catch((e: unknown) => {
      setDownloadError(e instanceof Error ? e.message : "Could not download PNG");
    });
  };

  return (
    <Stack spacing={3}>
      <MainCard title="QR code generator">
        <Stack spacing={2}>
          <Typography color="text.secondary">
            Paste a URL to generate a scannable QR code — copy the link or download the code as a PNG
            or SVG file (e.g. for a printed poster or handout).
          </Typography>
          <TextField
            label="URL"
            placeholder="https://…"
            fullWidth
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          {trimmed && matrix && svgMarkup ? (
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  p: 2,
                  bgcolor: "#fff",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  width: "fit-content",
                }}
                // The SVG markup is generated entirely by our own pure `qrMatrixToSvg` from a
                // boolean matrix + fixed numeric options — the typed URL is never interpolated
                // into the markup, only encoded as QR modules, so there's no injection surface.
                dangerouslySetInnerHTML={{ __html: svgMarkup }}
              />
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                <Button variant="outlined" onClick={handleCopy}>
                  {copyState === "copied" ? "Copied!" : copyState === "failed" ? "Couldn't copy" : "Copy URL"}
                </Button>
                <Button variant="outlined" onClick={handleDownloadPng}>
                  Download PNG
                </Button>
                <Button variant="outlined" onClick={handleDownloadSvg}>
                  Download SVG
                </Button>
              </Stack>
              {downloadError ? <Alert severity="error">{downloadError}</Alert> : null}
            </Stack>
          ) : (
            <Typography color="text.secondary" sx={{ fontStyle: "italic" }}>
              Enter a URL above to preview its QR code.
            </Typography>
          )}
        </Stack>
      </MainCard>

      {/*
       * Extension point — the REST of docs/356-ADMIN-UTILITIES-PAGE.md v0 (a LATER slice, not
       * built here, per this task's own explicit scope):
       *  - Tracked short-link (docs/356 §3.2): a Worker `GET /r/{slug}` redirect + a `short_links`
       *    D1 hit-counter, admin CRUD (`POST/GET/PATCH/DELETE /admin/short-links`), so "how many
       *    times was this QR/URL used" is trackable per provider — feeds docs/357/310/354.
       *    Needs a `contractVersion` bump + openapi/backend-api/data-model updates + a PHP-mirror
       *    flag (docs/270) when built.
       *  - Combine/merge audio (docs/356 §4): `ffmpeg.wasm` in a Web Worker, browser-only, a NEW
       *    admin-app-only dependency (lazy-loaded only when this tool opens); a link-out to the
       *    owner's separate scripts/tools repo for jobs past the browser ceiling.
       *  - Split/merge PDF (docs/356 §5): `pdf-lib` in a Web Worker, browser-only, a NEW
       *    admin-app-only dependency; no Worker endpoint (in-browser only in v0).
       *  - Future A/V editor (docs/356 §6): paid/tier-gated (docs/344), deferred until the
       *    combine-audio/split-PDF tools prove demand and a native processing service exists.
       * Each new tool gets its own `AdminFeatureId` flag family per docs/356 §7
       * (`utilities.qr`/`utilities.shortlinks`/`utilities.audio`/`utilities.pdf`/`utilities.avEditor`)
       * once built — this slice ships under the single coarse `utilities` id already registered.
       */}
    </Stack>
  );
}
